import { useEffect } from 'react';
import { throttleToAnimationFrame } from '@utils/common';
import { useSelf } from '@utils/react';
import { InsertionTypes, appendRow, replaceRow, prependRow } from '../utils/InsertionMethods.js';
import { shallowCompare } from '../utils/shallowCompare.js';

/**
 * Hook that only renderes the minimum amount of rows to fill the current
 * viewport in order to preserve performance in very long tables.
 */
function useVirtualizedTable({
  renderRow,
  rowComparator = shallowCompare,
  contentKey,
  rows,
  rowHeight,
  extraRowsToRender,
  tableRef,
  bodyRef
}) {
  const self = useSelf({
    startIndex: 0,
    rowsToRender: 0,
    scrollTop: 0,
    offset: 0,
    prevRows: []
  });
  useEffect(() => {
    const table = tableRef.current;
    const body = bodyRef.current;
    if (!body || !table) {
      return;
    }
    self.offset = extraRowsToRender * rowHeight / 2;
    self.rowsToRender = Math.ceil((window.innerHeight + self.offset * 2) / rowHeight);

    /**
     * Renders and inserts a specified amount of rows in the specified
     * direction. If deleteOpposite is set to true, then the row in the
     * opposite side will be removed in favor of the newly inserted row.
     *
     * When doing this action, a calculated padding will also be applied to the
     * top and bottom of the table in order to preserve scrollbar positioning.
     */
    const insertRows = (startIndex, amount, insertionType, deleteOpposite) => {
      if (insertionType === InsertionTypes.PREPEND) {
        const startInsertionIndex = Math.max(-1, startIndex - 1);
        const endInsertionIndex = Math.max(-1, startIndex - amount - 1);
        if (startInsertionIndex === endInsertionIndex) {
          return;
        }
        for (let i = startInsertionIndex; i > endInsertionIndex; i--) {
          prependRow({
            bodyRef,
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            rowData: rows[i],
            rowIndex: i,
            renderRow,
            deleteOpposite
          });
        }
      } else {
        // During initial appending we have not inserted our rowsToRender yet,
        // and hence our startIndex is the actual first index to be rendered
        const startInsertionIndex = Math.min(rows.length, insertionType === InsertionTypes.INITIAL ? startIndex : startIndex + self.rowsToRender);
        const endInsertionIndex = Math.min(rows.length, startInsertionIndex + amount);
        if (startInsertionIndex === endInsertionIndex) {
          return;
        }
        for (let i = startInsertionIndex; i < endInsertionIndex; i++) {
          appendRow({
            bodyRef,
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            rowData: rows[i],
            renderRow,
            rowIndex: i,
            deleteOpposite
          });
        }
      }
      const startPadding = Math.max(0, self.startIndex * rowHeight);
      const endPadding = Math.max(0, (rows.length - (self.startIndex + self.rowsToRender)) * rowHeight);
      table.style.paddingTop = `${startPadding}px`;
      table.style.paddingBottom = `${endPadding}px`;
    };

    // In case this is our initial render, then insert the desired amount of
    // rows
    if (self.prevRows.length === 0) {
      body.innerHTML = "";
      insertRows(self.startIndex, self.rowsToRender, InsertionTypes.INITIAL);
    } else {
      // ... else, in case of a state update, then replace only the required
      // rows that have changed
      const startIndex = Math.max(0, Math.min(self.startIndex, rows.length));
      const amountToRender = Math.max(0, Math.min(rows.length - startIndex, startIndex + self.rowsToRender));
      for (let i = startIndex; i < amountToRender; i++) {
        const relativeRowIndex = i - startIndex;

        // In case the row doesn't exist in the DOM, then simply insert it
        // instead of attempting to replace it.
        if (!body.children[relativeRowIndex]) {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          appendRow({
            bodyRef,
            rowData: rows[i],
            rowIndex: i,
            renderRow
          });
          continue;
        }

        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        if (!rowComparator(rows[i], self.prevRows[i])) {
          replaceRow({
            bodyRef,
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            rowData: rows[i],
            renderRow,
            rowIndex: i,
            relativeRowIndex
          });
        }
      }
    }
    const scroller = document.scrollingElement;
    if (!scroller) {
      return;
    }

    /**
     * Based on the current scroll position we ensure that the correct rows are
     * rendered to the DOM.
     */
    const onScroll = throttleToAnimationFrame(() => {
      const prevStartIndex = self.startIndex;
      self.scrollTop = Math.max(0, scroller.scrollTop - self.offset);
      self.startIndex = Math.min(rows.length - self.rowsToRender, Math.ceil(self.scrollTop / rowHeight));
      const diff = self.startIndex - prevStartIndex;

      // In case we're scrolling down, then append rows
      if (diff > 0) {
        insertRows(prevStartIndex, Math.abs(diff), InsertionTypes.APPEND, true);
      } else if (diff < 0) {
        // ... else insert them
        insertRows(prevStartIndex, Math.abs(diff), InsertionTypes.PREPEND, true);
      }
    });
    scroller.addEventListener("scroll", onScroll);
    return () => {
      self.prevRows = rows;
      scroller.removeEventListener("scroll", onScroll);
    };
  }, [contentKey, rows, rowHeight, renderRow, rowComparator, self, tableRef, bodyRef, extraRowsToRender]);
}

export { useVirtualizedTable };
//# sourceMappingURL=useVirutalizedBody.js.map
